import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { FormProvider, useForm } from 'react-hook-form';
import { IRecruiterTeamNameAutoCompleteOption, amplitude } from 'wisestep-api';
import { WsDrawer } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import { useTokenContext } from '../../../context/TokenContext';
import { ClientAutocomplete } from '../../ClientAutocomplete';
import { ClientRecruiterAutocomplete } from '../../ClientRecruiterAutocomplete';
import { CreatedBwDatesFilter, WsAccordion } from '../components';
import AdvanceFilterFooterBar from '../components/AdvanceFilterFooterBar';
import HiringForTypeFilter from '../components/HIringForTypeFilter';
import JobBoardFilter from '../components/JobBoardFilter';
import JobListingBusinessUnitFilter from '../components/JobListingBusinessUnitFilter';
import JobTypeFilter from '../components/JobTypeFilter';
import JobWorkplacePreferenceFilter from '../components/JobWorkplacePreferenceFilter';
import LocationsFilter from '../components/LocationsFilter';
import RecruiterAutoCompleteInputFilter from '../components/RecruiterAutoCompleteInputFilter';
import RecruitersFilter from '../components/RecruitersFilter';
import StatusFilter from '../components/StatusFilter';
import TagsFilter from '../components/TagsFilter';
import TeamFilter from '../components/TeamFilter';
import VmsFilter from '../components/VmsFilter';
import { JobAdvanceFilterFormProps } from '../models';

interface JobsAdvancedFilterProps {
  open: boolean;
  onApplyFilters: (jobAdvanceFilterFormProps: JobAdvanceFilterFormProps) => void;
  filterClose(): void;
  defaultValues: JobAdvanceFilterFormProps;
}

const WsJobsAdvanceFilter = ({
  open,
  filterClose,
  onApplyFilters,
  defaultValues,
}: JobsAdvancedFilterProps) => {
  const workspace = useWorkspaceContext();
  const token = useTokenContext();
  const methods = useForm<JobAdvanceFilterFormProps>({ defaultValues });
  const { handleSubmit, reset } = methods;
  const onFormSubmit = (data: JobAdvanceFilterFormProps) => {
    amplitude('Job_Listing_Apply_Filters', {
      clickedOn: 'Apply',
      workspaceName: workspace.name,
      pageName: 'JobListing',
      planType: workspace.planType,
      pageUrl: window.location.href,
      interfaceType: 'Web',
    });
    onApplyFilters(data);
    filterClose();
  };

  return (
    <FormProvider {...methods}>
      <WsDrawer
        className="job-listing-advanced-filters"
        title="Advanced Filters"
        open={open}
        onClose={() => filterClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(onFormSubmit)}
          onReset={() => {
            reset();
            onFormSubmit({});
          }}
        >
          <Box pb={8}>
            <List>
              <LocationsFilter
                name="locations"
                wisestepApi={true}
                token={token.token}
                authorization={token.auth}
              />
              <WsAccordion title="Client">
                <ClientAutocomplete name="clientList" multiple={true} />
              </WsAccordion>
              <WsAccordion title="Client Recruiter">
                <ClientRecruiterAutocomplete
                  name="clientRecruiterList"
                  multiple={true}
                  clients={methods.watch('clientList') ?? []}
                />
              </WsAccordion>
              <WsAccordion title="Workplace Preference">
                <JobWorkplacePreferenceFilter name="workplacePreference" />
              </WsAccordion>
              <JobTypeFilter name="jobType" />
              <HiringForTypeFilter name="hiringFor" />
              <VmsFilter name="jobSource" />
              <TagsFilter name="tags" defaultValue={methods.getValues('tags')} />
              <RecruitersFilter name="recruiters" defaultValue={methods.getValues('recruiters')} />
              <JobBoardFilter name="jobBoards" />
              <StatusFilter name="jobStatus" />
              <JobListingBusinessUnitFilter name="businessUnit" />
              <TeamFilter
                name="teams"
                defaultValue={methods.getValues('teams')?.map((obj) => obj.name as string) ?? []}
                defaultObjects={
                  methods.getValues('teams') as IRecruiterTeamNameAutoCompleteOption[]
                }
              />
              <RecruiterAutoCompleteInputFilter
                name="postedOnJobBoardBy"
                title="Posted On Job Board By"
              />
              <CreatedBwDatesFilter
                title="Posted On Job Boards Between"
                name="postedOnJobBoardFromDate"
                name1="postedOnJobBoardToDate"
              />
            </List>
          </Box>
          <AdvanceFilterFooterBar />
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};
export default WsJobsAdvanceFilter;
